import { z } from 'zod';

/**
 * Shared Zod schemas. Used client-side for instant UX feedback and server-side
 * as the authoritative gate — the server re-validates everything regardless of
 * what the client already checked.
 */

// Allow letters (incl. accented/unicode), spaces, apostrophes, hyphens, periods.
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}\s'’.-]{0,58}[\p{L}\p{M}.]$|^[\p{L}\p{M}]$/u;

export const nameSchema = z
  .string()
  .trim()
  .min(1, 'required')
  .max(60, 'too_long')
  .refine((v) => NAME_RE.test(v), 'invalid_characters');

export const phoneInputSchema = z.string().trim().min(7, 'invalid_phone').max(20, 'invalid_phone');

export const emailInputSchema = z.string().trim().min(3, 'invalid_email').max(254, 'invalid_email');

export const honeypotSchema = z.string().max(0, 'bot_detected').optional().default('');

export const lookupRequestSchema = z.object({
  phone: phoneInputSchema,
});

export const registerRequestSchema = z.object({
  phone: phoneInputSchema,
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailInputSchema,
  requestedBarberId: z.number().int().positive().nullable(),
  marketingConsent: z.boolean().default(false),
  source: z.enum(['WEB', 'KIOSK']),
  language: z.enum(['en', 'es']).default('en'),
  website: honeypotSchema, // honeypot field; real users never fill this in
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const returningRegisterRequestSchema = z.object({
  phone: phoneInputSchema,
  requestedBarberId: z.number().int().positive().nullable(),
  source: z.enum(['WEB', 'KIOSK']),
  language: z.enum(['en', 'es']).default('en'),
  website: honeypotSchema,
  updatedFirstName: nameSchema.optional(),
  updatedLastName: nameSchema.optional(),
  updatedEmail: emailInputSchema.optional(),
});
export type ReturningRegisterRequest = z.infer<typeof returningRegisterRequestSchema>;

export const staffLoginRequestSchema = z.object({
  pin: z.string().min(4).max(12),
});

export const reassignRequestSchema = z.object({
  requestedBarberId: z.number().int().positive().nullable(),
});

export const barberStatusRequestSchema = z.object({
  liveStatus: z.enum(['AVAILABLE', 'ON_BREAK', 'DONE_FOR_DAY']),
});

export const adminBarberUpdateSchema = z.object({
  displayName: z.string().trim().min(1).max(60).optional(),
  active: z.boolean().optional(),
  displayOrder: z.number().int().min(0).max(999).optional(),
});

export const adminScheduleEntrySchema = z.object({
  weekday: z.number().int().min(0).max(6),
  enabled: z.boolean(),
  startMinutes: z.number().int().min(0).max(1439).nullable(),
  endMinutes: z.number().int().min(0).max(1439).nullable(),
});

export const adminScheduleOverrideSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  enabled: z.boolean(),
  startMinutes: z.number().int().min(0).max(1439).nullable(),
  endMinutes: z.number().int().min(0).max(1439).nullable(),
  notes: z.string().max(200).optional(),
});

export const adminHoursOverrideSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  open: z.boolean(),
  startMinutes: z.number().int().min(0).max(1439).nullable(),
  endMinutes: z.number().int().min(0).max(1439).nullable(),
  queueAcceptanceOverride: z.boolean().nullable().optional(),
  notes: z.string().max(200).optional(),
});

export const adminBusinessHoursSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  open: z.boolean(),
  startMinutes: z.number().int().min(0).max(1439).nullable(),
  endMinutes: z.number().int().min(0).max(1439).nullable(),
});

export const adminQueueControlSchema = z.object({
  queueEnabled: z.boolean(),
  message: z.string().max(200).optional().nullable(),
});

export const adminCustomerUpdateSchema = z.object({
  firstName: nameSchema.optional(),
  lastName: nameSchema.optional(),
  email: emailInputSchema.optional(),
  phone: phoneInputSchema.optional(),
  marketingConsent: z.boolean().optional(),
});

export const adminManualVisitSchema = z.object({
  phone: phoneInputSchema,
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailInputSchema,
  requestedBarberId: z.number().int().positive().nullable(),
  marketingConsent: z.boolean().default(false),
  isPresent: z.boolean().default(true),
});
